import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Activity, CheckCircle2, Loader2, Lock, Play, TriangleAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/cn';
import { useDisruptionProgress, useResolveNext } from '@/lib/disruptions/use-disruptions';
import type {
  DisruptionGroupFacts,
  DisruptionProgress,
  ResolutionOp,
  ResolutionState,
  ResolutionStep,
  ResolveNextResult,
} from '@/lib/disruptions/types';
import { formatPriceMinor } from './labels';
import { RebookingProgressDialog } from './rebooking-progress-dialog';
import {
  STATUS_CHIP,
  skippedChip,
  skippedIsPositive,
  stepCounter,
  stepIcon,
  stepLabel,
  stepState,
  stepSubject,
} from './resolution-steps';

// disruption-overview — the one-click "RESOLVE" hero.
//
// ONE card: a 6-node rail (rebook · tickets · hotels · vouchers · acceptance ·
// contain) with live counters, ONE primary button labelled with the concrete
// next action (`nextStep.cta`, e.g. "Rebook 188 groups"), ONE status line, and a
// LIVE strip while a step runs (bar · N/M · % · elapsed · ~k/min · why). Drives the
// FROZEN B2 `resolve-next@v1` seam one op at a time:
//   • reversible steps (rebook, hotels) fire on ONE click and open the live dialog;
//   • committing steps (tickets, acceptance, contain) confirm first (tally + copy),
//     then fire with `expectedOp` so a state that moved on can never fire the wrong op.
// Never a false terminal state: "contained" only when contain is DONE.

interface ResolutionRunProps {
  disruptionUrn: string;
  state: ResolutionState;
}

/** Steps that COMMIT (irreversible) — confirm before firing. */
const COMMIT_OPS: ReadonlySet<ResolutionOp> = new Set<ResolutionOp>([
  'tickets',
  'acceptance',
  'contain',
]);
/** Steps whose live progress has a dialog (durable domain runs). */
const DIALOG_OP: Partial<Record<ResolutionOp, 'rebooking' | 'allocation'>> = {
  rebooking: 'rebooking',
  hotels: 'allocation',
};

export function ResolutionRun({ disruptionUrn, state }: ResolutionRunProps) {
  const { t } = useTranslation('rebooking');
  const resolve = useResolveNext(disruptionUrn);
  const { data: live } = useDisruptionProgress(disruptionUrn);

  // The last REAL fire — drives the status line until the aggregate moves on.
  const [fired, setFired] = useState<ResolveNextResult | null>(null);
  // Confirmation for committing steps (null = closed).
  const [confirmOp, setConfirmOp] = useState<ResolutionOp | null>(null);
  // Live dialog for a durable run just fired (or opened via "View live").
  const [dialogOp, setDialogOp] = useState<'rebooking' | 'allocation' | null>(null);

  const next = state.nextStep;
  const nextOp = next.op;
  const activeRun = live?.activeRun ?? state.progress.activeRun ?? null;
  const runInFlight = !!activeRun;
  const contained = state.steps.find((s) => s.op === 'contain')?.status === 'DONE';
  const mode = next.mode ?? (nextOp === 'none' ? 'done' : 'start');
  const actionable = (next.actionable ?? nextOp !== 'none') && !runInFlight && !resolve.isPending;

  const cta =
    runInFlight && activeRun
      ? runningCta(activeRun.op, activeRun.progress, live, t)
      : nextOp === 'none'
        ? t('run.ctaContained')
        : (next.cta ?? stepLabel(nextOp));

  // Clear the optimistic "fired" state once the aggregate advanced past the fired op.
  useEffect(() => {
    if (fired && fired.startedOp !== 'none' && nextOp !== fired.startedOp) setFired(null);
  }, [fired, nextOp]);

  function fire(op: ResolutionOp): void {
    resolve.mutate(
      { dryRun: false, expectedOp: op },
      {
        onSuccess: (res) => {
          setConfirmOp(null);
          setFired(res);
          const dialog = DIALOG_OP[op];
          if (res.startedOp === op && dialog) setDialogOp(dialog);
        },
      },
    );
  }

  function onPrimary(): void {
    if (nextOp === 'none' || !actionable) return;
    if (COMMIT_OPS.has(nextOp)) {
      setConfirmOp(nextOp);
      return;
    }
    fire(nextOp);
  }

  const lastDurable: 'rebooking' | 'allocation' | null = useMemo(() => {
    if (activeRun?.op === 'rebooking' || activeRun?.op === 'allocation') return activeRun.op;
    if (live?.allocationRun) return 'allocation';
    if (live?.runStatus && live.runStatus !== 'NONE') return 'rebooking';
    return null;
  }, [activeRun?.op, live?.allocationRun, live?.runStatus]);

  return (
    <Card data-testid="resolution-run" className="shrink-0">
      <CardContent className="flex flex-col gap-2.5 p-3">
        <div className="flex items-center gap-1.5">
          {contained ? (
            <CheckCircle2 className="h-4 w-4 text-emerald-600" aria-hidden />
          ) : (
            <Play className="h-4 w-4 text-primary" aria-hidden />
          )}
          <h2 className="text-sm font-semibold text-foreground">
            {contained ? t('run.containedTitle') : t('run.title')}
          </h2>
          {!contained ? (
            <span className="hidden text-xs text-muted-foreground sm:inline">
              {t('run.subtitle')}
            </span>
          ) : null}
        </div>
        <Rail
          steps={state.steps}
          facts={state.kpis.groups ?? null}
          currentOp={runInFlight && activeRun ? railOpFor(activeRun.op) : nextOp}
        />

        <div className="flex flex-wrap items-center gap-2">
          {contained ? null : (
            <Button
              size="lg"
              className={cn('gap-2', mode === 'retry' && 'bg-amber-600 hover:bg-amber-700')}
              disabled={!actionable || nextOp === 'none'}
              onClick={onPrimary}
              data-testid="resolution-run-trigger"
              title={!actionable && next.reason ? next.reason : undefined}
            >
              {resolve.isPending || runInFlight ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              ) : nextOp === 'none' ? (
                <CheckCircle2 className="h-4 w-4" aria-hidden />
              ) : mode === 'blocked' || mode === 'unknown' ? (
                <Lock className="h-4 w-4" aria-hidden />
              ) : (
                <Play className="h-4 w-4" aria-hidden />
              )}
              {cta}
            </Button>
          )}
          {lastDurable ? (
            <Button
              size="sm"
              variant="ghost"
              className="gap-1.5 text-xs"
              onClick={() => setDialogOp(lastDurable)}
              data-testid="resolution-run-live"
            >
              <Activity className="h-3.5 w-3.5" aria-hidden />
              {t('run.viewLive')}
            </Button>
          ) : null}
        </div>

        <StatusLine
          state={state}
          live={live}
          fired={fired}
          isError={resolve.isError}
          contained={contained}
        />

        {activeRun ? <LiveStrip live={live} state={state} /> : null}
      </CardContent>

      <ConfirmDialog
        op={confirmOp}
        state={state}
        isFiring={resolve.isPending}
        onConfirm={() => confirmOp && fire(confirmOp)}
        onCancel={() => setConfirmOp(null)}
      />
      {dialogOp ? (
        <RebookingProgressDialog
          disruptionUrn={disruptionUrn}
          open={!!dialogOp}
          onOpenChange={(open) => (open ? undefined : setDialogOp(null))}
          op={dialogOp}
        />
      ) : null}
    </Card>
  );
}

function railOpFor(runOp: string): ResolutionOp | 'none' {
  if (runOp === 'allocation') return 'hotels';
  if (
    runOp === 'rebooking' ||
    runOp === 'tickets' ||
    runOp === 'acceptance' ||
    runOp === 'vouchers'
  ) {
    return runOp;
  }
  return 'none';
}

function runningCta(
  op: string,
  progress: { done: number; total: number; unit: string } | undefined,
  live: DisruptionProgress | undefined,
  t: TFunction<'rebooking'>,
): string {
  switch (op) {
    case 'rebooking': {
      const done = live?.processedGroups ?? 0;
      const total = live?.plannedGroups ?? 0;
      return total > 0 ? t('run.cta.rebookingProgress', { done, total }) : t('run.cta.rebooking');
    }
    case 'allocation': {
      const a = live?.allocationRun;
      return a && a.totalGroups > 0
        ? t('run.cta.allocationProgress', {
            done: a.heldGroups + a.alreadyReservedGroups,
            total: a.totalGroups,
          })
        : t('run.cta.allocation');
    }
    case 'tickets':
      return progress
        ? t('run.cta.ticketsProgress', { done: progress.done, total: progress.total })
        : t('run.cta.tickets');
    case 'acceptance':
      return progress
        ? t('run.cta.acceptanceProgress', { done: progress.done, total: progress.total })
        : t('run.cta.acceptance');
    case 'vouchers':
      return t('run.cta.vouchers');
    default:
      return t('run.cta.running');
  }
}

/** The 6-node rail — icon + counter + tone per step; the current node is ringed. */
function Rail({
  steps,
  facts,
  currentOp,
}: {
  steps: ResolutionStep[];
  facts: DisruptionGroupFacts | null;
  currentOp: ResolutionOp | 'none';
}) {
  return (
    <ol className="flex flex-wrap items-center gap-1" data-testid="resolution-rail">
      {steps.map((s, i) => {
        const Icon = stepIcon(s.op);
        const chip = STATUS_CHIP[s.status] ?? STATUS_CHIP.UNKNOWN;
        const st = stepState(s, facts, steps);
        const current = s.op === currentOp;
        return (
          <li key={s.op} className="flex items-center gap-1">
            <div
              className={cn(
                'flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs',
                current ? 'border-primary ring-1 ring-primary/40' : 'border-border',
                s.status === 'SKIPPED' && !skippedIsPositive(s) && 'opacity-60',
              )}
              title={`${stepSubject(s.op)} · ${st.label}${st.detail ? ` · ${st.detail}` : ''}`}
              data-testid={`resolution-rail-${s.op}`}
              data-status={s.status}
            >
              <span
                className={cn(
                  'inline-flex h-4 w-4 items-center justify-center rounded-full',
                  // A skip worth reading wears its own colour (hotels "not needed"
                  // emerald, a policy-disabled rebooking indigo) — never the grey n/a.
                  skippedChip(s)?.className ?? chip.className,
                )}
              >
                {s.status === 'RUNNING' ? (
                  <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
                ) : (
                  <Icon className="h-3 w-3" aria-hidden />
                )}
              </span>
              <span className="font-medium text-foreground">{stepSubject(s.op)}</span>
              <span className="text-muted-foreground">{st.label.toLowerCase()}</span>
              {s.status !== 'SKIPPED' &&
              s.status !== 'BLOCKED' &&
              s.status !== 'UNKNOWN' &&
              s.op !== 'contain' ? (
                <span className="tabular-nums text-muted-foreground">{stepCounter(s)}</span>
              ) : null}
            </div>
            {i < steps.length - 1 ? <span className="text-muted-foreground/50">›</span> : null}
          </li>
        );
      })}
    </ol>
  );
}

/** The single status line beneath the control — mutually-exclusive UX states. */
function StatusLine({
  state,
  live,
  fired,
  isError,
  contained,
}: {
  state: ResolutionState;
  live: DisruptionProgress | undefined;
  fired: ResolveNextResult | null;
  isError: boolean;
  contained: boolean;
}) {
  const { t } = useTranslation('rebooking');
  const next = state.nextStep;
  const activeRun = live?.activeRun ?? state.progress.activeRun ?? null;

  if (isError) {
    return (
      <p
        className="flex items-center gap-1.5 text-xs text-destructive"
        data-testid="resolution-run-error"
      >
        <TriangleAlert className="h-3.5 w-3.5 shrink-0" aria-hidden />
        {t('run.status.error')}
      </p>
    );
  }
  // A held disruption lock comes back on a 200 as startedOp:'none' with "already in
  // progress" — keep in lockstep with operator-bff resolve-next.service.ts LOCK_HELD_REASON.
  if (fired?.startedOp === 'none' && fired.reason.includes('already in progress')) {
    return (
      <p
        className="flex items-center gap-1.5 text-xs text-muted-foreground"
        data-testid="resolution-run-in-flight"
      >
        <Lock className="h-3.5 w-3.5 shrink-0" aria-hidden />
        {t('run.status.inFlight')}
      </p>
    );
  }
  if (activeRun) {
    return (
      <p
        className="flex items-center gap-1.5 text-xs text-foreground"
        data-testid="resolution-run-executing"
      >
        <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-primary" aria-hidden />
        {runningLine(activeRun.op, live, activeRun.operatorUrn, t)}
      </p>
    );
  }
  // Stale expectedOp — keep in lockstep with operator-bff resolve-next.service.ts
  // ("The state changed — the next step is now …").
  if (fired?.startedOp === 'none' && fired.reason.startsWith('The state changed')) {
    return (
      <p
        className="flex items-center gap-1.5 text-xs text-amber-700 dark:text-amber-300"
        data-testid="resolution-run-blocked"
      >
        <TriangleAlert className="h-3.5 w-3.5 shrink-0" aria-hidden />
        {fired.reason}
      </p>
    );
  }
  if (contained || next.op === 'none') {
    return (
      <p
        className="flex items-center gap-1.5 text-xs text-emerald-700 dark:text-emerald-300"
        data-testid="resolution-run-resolved"
      >
        <CheckCircle2 className="h-3.5 w-3.5 shrink-0" aria-hidden />
        {t('run.status.contained', { reason: next.reason })}
      </p>
    );
  }
  if (next.mode === 'blocked' || next.mode === 'unknown' || next.mode === 'wait') {
    return (
      <p
        className="flex items-center gap-1.5 text-xs text-amber-700 dark:text-amber-300"
        data-testid="resolution-run-blocked"
      >
        <TriangleAlert className="h-3.5 w-3.5 shrink-0" aria-hidden />
        {stepLabel(next.op)}: {next.reason}
      </p>
    );
  }
  if (next.actionable === false) {
    return (
      <p
        className="flex items-center gap-1.5 text-xs text-muted-foreground"
        data-testid="resolution-run-blocked"
      >
        <Lock className="h-3.5 w-3.5 shrink-0" aria-hidden />
        {next.reason}
      </p>
    );
  }
  return (
    <p
      className="flex items-center gap-1.5 text-xs text-muted-foreground"
      data-testid="resolution-run-next"
    >
      <Play className="h-3.5 w-3.5 shrink-0" aria-hidden />
      {t('run.status.next', { step: stepLabel(next.op), reason: next.reason })}
    </p>
  );
}

function runningLine(
  op: string,
  live: DisruptionProgress | undefined,
  operatorUrn: string,
  t: TFunction<'rebooking'>,
): string {
  const who =
    operatorUrn && operatorUrn !== 'unknown'
      ? ` ${t('run.line.by', { name: operatorUrn.split('|').pop() ?? operatorUrn })}`
      : '';
  switch (op) {
    case 'rebooking': {
      const done = live?.processedGroups ?? 0;
      const total = live?.plannedGroups ?? 0;
      const delayed = live?.delayedBy?.length
        ? ` ${t('run.line.delayedBy', { vendors: live.delayedBy.join(', ') })}`
        : '';
      return `${t('run.line.rebooking', { done, total })}${delayed}${who}`;
    }
    case 'allocation': {
      const a = live?.allocationRun;
      if (a && a.processedGroups === 0) return `${t('run.line.allocationSearching')}${who}`;
      return a
        ? `${t('run.line.allocation', {
            done: a.heldGroups + a.alreadyReservedGroups,
            total: a.totalGroups,
          })}${a.soldOutGroups ? ` ${t('run.line.allocationSoldOut', { count: a.soldOutGroups })}` : ''}${who}`
        : `${t('run.cta.allocation')}${who}`;
    }
    case 'tickets': {
      const p = live?.activeRun?.progress;
      const g = live?.groups && 'ticketed' in live.groups ? live.groups : null;
      return `${t('run.cta.ticketsProgress', { done: p?.done ?? 0, total: p?.total ?? 0 })}${g ? ` ${t('run.line.ticketsIssued', { count: g.ticketed })}` : ''}${who}`;
    }
    case 'acceptance': {
      const p = live?.activeRun?.progress;
      const g = live?.groups && 'accepted' in live.groups ? live.groups : null;
      return `${t('run.line.acceptance', { done: p?.done ?? 0, total: p?.total ?? 0 })}${g ? ` ${t('run.line.acceptanceAccepted', { count: g.accepted })}` : ''}${who}`;
    }
    case 'vouchers':
      return `${t('run.line.vouchers')}${who}`;
    default:
      return `${t('run.cta.running')}${who}`;
  }
}

/** Live strip: bar · N/M · % · elapsed · ~k/min · per-op counters. WS-fed; the only timer is the wall clock. */
function LiveStrip({
  live,
  state,
}: {
  live: DisruptionProgress | undefined;
  state: ResolutionState;
}) {
  const { t } = useTranslation('rebooking');
  const activeRun = live?.activeRun ?? state.progress.activeRun ?? null;
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  if (!activeRun) return null;
  let done = 0;
  let total = 0;
  let unit = t('run.strip.groups');
  let counters: string[] = [];
  if (activeRun.op === 'rebooking') {
    done = live?.processedGroups ?? 0;
    total = live?.plannedGroups ?? 0;
    counters = [
      t('run.strip.withFlight', { count: live?.assignedGroups ?? 0 }),
      ...(live?.noOptionGroups ? [t('run.strip.noOptions', { count: live.noOptionGroups })] : []),
      ...(live?.degradedGroups ? [t('run.strip.degraded', { count: live.degradedGroups })] : []),
    ];
  } else if (activeRun.op === 'allocation') {
    const a = live?.allocationRun;
    done = a?.processedGroups ?? 0;
    total = a?.totalGroups ?? 0;
    counters = a
      ? [
          t('run.strip.onHold', { count: a.heldGroups }),
          ...(a.soldOutGroups ? [t('run.strip.noCapacity', { count: a.soldOutGroups })] : []),
          ...(a.noOfferGroups
            ? [t('run.strip.noEligibleHotel', { count: a.noOfferGroups })]
            : []),
        ]
      : [];
  } else {
    done = activeRun.progress?.done ?? 0;
    total = activeRun.progress?.total ?? 0;
    unit = t('run.strip.cases');
    const g = live?.groups && 'groups' in live.groups ? live.groups : null;
    if (g) {
      counters =
        activeRun.op === 'tickets'
          ? [t('run.strip.tickets', { done: g.ticketed, total: g.assigned })]
          : activeRun.op === 'acceptance'
            ? [
                t('run.strip.accepted', { done: g.accepted, total: g.atGate }),
                t('run.strip.hotelsConfirmed', { count: g.hotelConfirmed }),
              ]
            : [t('run.strip.vouchers', { done: g.voucherIssued, total: g.voucherEligible })];
    }
  }
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const elapsed = Math.max(0, Math.round((now - Date.parse(activeRun.startedAt)) / 1000));
  const perMin = elapsed >= 5 && done > 0 ? Math.max(1, Math.round((done / elapsed) * 60)) : null;
  const eta = perMin && total > done ? Math.ceil(((total - done) / perMin) * 60) : null;
  return (
    <div
      className="rounded-md border border-border bg-muted/30 px-3 py-2"
      data-testid="resolution-live-strip"
    >
      <div className="flex items-center gap-3">
        <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
          <div
            className="h-full rounded-full bg-primary transition-[width]"
            style={{ width: `${pct}%` }}
          />
        </div>
        <span className="w-10 text-right text-xs font-semibold tabular-nums">{pct}%</span>
      </div>
      <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
        <span className="tabular-nums">
          {done}/{total} {unit}
        </span>
        <span className="tabular-nums">{fmtSecs(elapsed)}</span>
        {perMin ? <span>~{perMin}/min</span> : null}
        {eta != null ? <span>{t('run.strip.eta', { time: fmtSecs(eta) })}</span> : null}
        {counters.map((c) => (
          <span key={c}>{c}</span>
        ))}
      </p>
    </div>
  );
}

function fmtSecs(s: number): string {
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
}

/** Confirmation for the COMMITTING steps (tickets · acceptance · contain). */
function ConfirmDialog({
  op,
  state,
  isFiring,
  onConfirm,
  onCancel,
}: {
  op: ResolutionOp | null;
  state: ResolutionState;
  isFiring: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation('rebooking');
  const open = !!op;
  const g = state.kpis.groups;
  const Icon = op ? stepIcon(op) : Play;
  const cases = state.kpis.caseCount;
  // airline-money@v2 — the held hotels at the airline's PRICE; null → «No disponible».
  const heldPriceMinor = state.kpis.heldPriceMinor;
  const currency = state.kpis.currency;
  const step = state.steps.find((s) => s.op === op);
  const n = step?.remaining ?? step?.total ?? 0;
  const forced = op === 'contain' && step?.containMode !== 'NATURAL';
  const title =
    op === 'tickets'
      ? t('run.confirm.ticketsTitle', { issued: n, count: cases })
      : op === 'acceptance'
        ? t('run.confirm.acceptanceTitle', { count: n })
        : forced
          ? t('run.confirm.forceTitle')
          : t('run.confirm.naturalTitle');
  const body =
    op === 'tickets'
      ? t('run.confirm.ticketsBody')
      : op === 'acceptance'
        ? t('run.confirm.acceptanceBody', { price: formatPriceMinor(heldPriceMinor, currency, t) })
        : forced
          ? t('run.confirm.forceBody', {
              held: g?.hotelHeld ? ` (${g.hotelHeld})` : '',
              assigned: g?.assigned ? ` (${g.assigned})` : '',
              residuals: residualsLine(state, t),
            })
          : t('run.confirm.naturalBody', { residuals: residualsLine(state, t) });
  const button =
    op === 'tickets'
      ? t('run.confirm.ticketsButton')
      : op === 'acceptance'
        ? t('run.confirm.acceptanceButton')
        : forced
          ? t('run.confirm.forceButton')
          : t('run.confirm.naturalButton');

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? undefined : onCancel())}>
      <DialogContent data-testid="resolution-run-confirm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon className="h-4 w-4 text-primary" aria-hidden />
            {title}
          </DialogTitle>
          <DialogDescription data-testid="resolution-run-confirm-reason">{body}</DialogDescription>
        </DialogHeader>
        {g ? (
          <div
            className="rounded-lg border border-border bg-muted/40 p-3 text-xs text-muted-foreground"
            data-testid="resolution-run-confirm-implications"
          >
            {t('run.confirm.implications', {
              assigned: g.assigned,
              rebookable: g.rebookable,
              ticketed: g.ticketed,
              hotelHeld: g.hotelHeld,
              hotelConfirmed: g.hotelConfirmed,
              accepted: g.accepted,
              atGate: g.atGate,
            })}
          </div>
        ) : null}
        <DialogFooter>
          <Button
            variant="outline"
            onClick={onCancel}
            disabled={isFiring}
            data-testid="resolution-run-confirm-cancel"
          >
            {t('common:common.cancel')}
          </Button>
          <Button onClick={onConfirm} disabled={isFiring} data-testid="resolution-run-confirm-fire">
            {isFiring ? (
              <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <Play className="mr-1.5 h-4 w-4" aria-hidden />
            )}
            {button}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function residualsLine(state: ResolutionState, t: TFunction<'rebooking'>): string {
  const p = state.problems;
  const parts: string[] = [];
  const noOptions = Object.values(p.noOptionsByReason ?? {}).reduce((a, b) => a + b, 0);
  if (noOptions) parts.push(t('run.strip.noOptions', { count: noOptions }));
  if (p.allocationGaps) parts.push(t('run.residuals.noHotel', { count: p.allocationGaps }));
  if (p.declined) parts.push(t('run.residuals.declined', { count: p.declined }));
  if (p.unticketed) parts.push(t('run.residuals.unticketed', { count: p.unticketed }));
  return parts.length
    ? ` ${t('run.residuals.line', { count: parts.length, parts: parts.join(' · ') })}`
    : '';
}
